/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ISignInItem, ISignInLoading, ISignInLocalAccount, ISignInSocialAccount, ISignInSocialItem,
    ISignInViewProps
} from '@msdyn365-commerce-modules/account-management/src/modules/sign-in/./sign-in';

import { ISignInConfig, ISignInProps } from '../definition-extensions/sign-in.ext.props.autogenerated';
import msdyn365Commerce from '@msdyn365-commerce/core-internal';


const LocalAccount: React.FC<ISignInLocalAccount> = ({
    localAccount,
    items,
    forgetPassword,
    disclaimer,
    error,
    signInButton
}) => {

    return (
        <Node {...localAccount}>
            {disclaimer}
            {items && items.map((item: ISignInItem) => {
                return (
                    <Node key={item.key} {...item.wrapper}>
                        {item.label}
                        {item.error}
                        {item.input}
                    </Node>
                );
            })}
            {forgetPassword}
            {error}
            {signInButton}
        </Node>
    );
};

const SocialAccount: React.FC<ISignInSocialAccount> = ({
    socialAccounts,
    items
}) => {

    return (
        <Node {...socialAccounts}>
            {items && items.map((item: ISignInSocialItem) => {
                return (
                    <Node key={item.key} {...item.button}>
                        {item.image}
                        {item.text}
                    </Node>
                );
            })}
        </Node>
    );
};

const SignInLoading: React.FC<ISignInLoading> = ({
    modal,
    modalBody,
    icon,
    message
}) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};


const signUpNilecardDescription = (langstrings: string[]): React.ReactNode => {
    return (
        <>
        {langstrings[0]}
        <ul>
            <li>{langstrings[1]}</li>
            <li>{langstrings[2]}</li>
            <li>{langstrings[3]}</li>
            <li>{langstrings[4]}</li>
        </ul>
    </>
    );
};


const SignInView: React.FC<ISignInViewProps & ISignInProps<ISignInConfig>> = props => {

    const {
        signIn,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        signInSection,
        signUpSection,
        signInSectionHeading,
        signInLocalAccount,
        signInSocialAccount,
        signUpSectionHeading,
        // signUpDescription,
        signUpLink,
        resources
    } = props;
    let langver;
    if (msdyn365Commerce.isBrowser){
        const params = new URLSearchParams(window.location.search);
        let lang = params.get('ui_locales');
        // console.log(lang);
        switch (lang){
            case "en-US":
                langver = ["Register for a Nilecard and benefit from many advantages:",
                "Receive up to 10% discount with the Nilecard points system",
                "Receive discount vouchers and invitations to special events", 
                "Save products on your wish list for later", 
                "Track your order history and points balance in your account"];
                break;
            case "fr-CH":
                langver = ["Crée une Nilecard et profite de nombreux avantages:",
                "Reçois jusqu'à 10% de réduction avec le système de points Nilecard",
                "Reçois des bons de réduction et des invitations à des événements spéciaux",
                "Enregistre des produits sur ta liste d'envies pour plus tard",
                "Suis les commandes et le solde de points dans ton compte"]
                break;
            case "de-CH":
                langver =  ["Erstelle ein NILE-Kundenkonto und profitiere von vielen Vorteilen:",
                "Erhalte bis zu 10% Rabatt mit dem Nilecard Punktesystem", 
                "Erhalte Rabattgutscheine und Einladungen zu speziellen Events", 
                "Speichere Produkte auf deiner Wunschliste für später", 
                "Verfolge deinen Bestellverlauf und Punktestand in deinem Konto"];
                break;
            default:
                langver = [resources.liLead, resources.liOne, resources.liTwo, resources.liThree, resources.liFour];
                break;
        }
    }

    return (
        <Module {...signIn}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignInLoading {...loading} />}
            <Node {...aadConainer}>
                <Node {...signInSection}>
                    {signInSectionHeading}
                    {LocalAccount(signInLocalAccount)}
                    {SocialAccount(signInSocialAccount)}
                </Node>
                <Node {...signUpSection}>
                    {signUpSectionHeading}
                    {/* {signUpDescription} */}
                    {langver && signUpNilecardDescription(langver)}
                    {signUpLink}
                </Node>
            </Node>
        </Module>
    );
};

export default SignInView;
